import { useState } from 'react';
import { Link } from 'react-router-dom';
import LanguageSelector from '../../components/feature/LanguageSelector';

const EDGE_BASE = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

export default function SignupPage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [emailSentOk, setEmailSentOk] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      // All signup logic runs server-side via the edge function:
      // 1. Creates auth user with email_confirm: true (no confirmation email needed)
      // 2. Inserts client record using service role (bypasses RLS)
      // 3. Creates retainer record
      // 4. Sends branded welcome email via Resend
      // 5. Notifies admin
      const res = await fetch(`${EDGE_BASE}/functions/v1/client-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          company: formData.company.trim() || null,
          phone: formData.phone.trim() || null,
        }),
      });

      const data = await res.json();
      console.log('[signup] edge function response:', data);

      if (!res.ok || data.error) {
        throw new Error(data.error || 'Signup failed. Please try again.');
      }

      setEmailSentOk(!!data.emailSent);
      setSignupSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  // ─── SUCCESS SCREEN ────────────────────────────────────────────────────────
  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0d1520] to-[#111827] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-4">
            <LanguageSelector prominent />
          </div>

          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6 cursor-pointer">
              <h1 className="text-3xl font-black text-[#FDB913] tracking-wide">SERENITY LAW</h1>
              <p className="text-white/60 text-sm font-semibold tracking-widest uppercase">Client Portal</p>
            </Link>
          </div>

          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 shadow-2xl space-y-6">
            {/* Icon */}
            <div className="w-20 h-20 flex items-center justify-center bg-green-500/20 rounded-full mx-auto">
              <i className="ri-checkbox-circle-line text-green-400 text-4xl"></i>
            </div>

            {/* Headline */}
            <div className="text-center">
              <h2 className="text-2xl font-black text-white mb-2">Account Created!</h2>
              <p className="text-white/70 text-sm leading-relaxed">
                Welcome to Serenity Law PLLC,{' '}
                <strong className="text-[#FDB913]">{formData.name}</strong>.
              </p>
            </div>

            {/* Verified badge */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3">
              <div className="flex items-start gap-2">
                <i className="ri-shield-check-line text-green-400 text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-green-300 text-xs leading-relaxed">
                  <strong className="text-white">Your email is verified and your account is active.</strong>{' '}
                  You can log in right now — no confirmation link needed.
                </p>
              </div>
            </div>

            {/* Email status */}
            <div className={`border rounded-xl px-4 py-3 ${emailSentOk ? 'bg-[#FDB913]/5 border-[#FDB913]/20' : 'bg-white/5 border-white/10'}`}>
              <div className="flex items-start gap-2">
                <i className={`text-sm flex-shrink-0 mt-0.5 ${emailSentOk ? 'ri-mail-check-line text-[#FDB913]' : 'ri-mail-line text-white/30'}`}></i>
                <p className={`text-xs leading-relaxed ${emailSentOk ? 'text-white/70' : 'text-white/40'}`}>
                  {emailSentOk
                    ? <>A welcome email has been sent to <strong className="text-[#FDB913]">{formData.email}</strong>. Check your inbox (and spam folder).</>
                    : <>Welcome email delivery is being processed. If you don&apos;t receive it, contact us at <strong className="text-white/60">engage@tariffrefund.attorney</strong>.</>
                  }
                </p>
              </div>
            </div>

            {/* Next steps */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
              <p className="text-white/80 text-xs font-black uppercase tracking-widest">Next Steps</p>
              {[
                'Log in with your email and password using the button below.',
                'Wait for attorney authorization to access your Engagement Letter.',
                'Fund your retainer and our team will begin your tariff refund case.',
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#FDB913] rounded-full text-black text-xs font-black flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">{step}</p>
                </div>
              ))}
            </div>

            {/* Go to login */}
            <Link
              to="/login"
              className="w-full bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-base py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(253,185,19,0.4)] flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-login-box-line"></i>
              Go to Login
            </Link>
          </div>

          <div className="text-center mt-4">
            <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">
              <i className="ri-arrow-left-line mr-1"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── SIGNUP FORM ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0d1520] to-[#111827] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-4">
          <LanguageSelector prominent />
        </div>

        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6 cursor-pointer">
            <h1 className="text-3xl font-black text-[#FDB913] tracking-wide">SERENITY LAW</h1>
            <p className="text-white/60 text-sm font-semibold tracking-widest uppercase">Client Portal</p>
          </Link>
        </div>

        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="mb-6">
            <h2 className="text-2xl font-black text-white mb-2">Create Account</h2>
            <p className="text-white/60 text-sm">Register to access your client portal</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your full name"
                required
                className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">Importer of Record *</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Legal name of your importing company"
                required
                className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1 (555) 000-0000"
                required
                className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                required
                className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">Password *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="At least 6 characters"
                required
                className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm"
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-white px-4 py-3 rounded-lg text-sm">
                <i className="ri-error-warning-line mr-2"></i>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-base py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(253,185,19,0.4)] flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <><i className="ri-loader-4-line animate-spin"></i>Creating Account...</>
              ) : (
                <><i className="ri-user-add-line"></i>Create Account</>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-white/60 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-[#FDB913] hover:text-white font-bold transition-colors cursor-pointer">
                Sign In
              </Link>
            </p>
          </div>

          <div className="mt-4 bg-black/30 border border-white/10 rounded-lg px-4 py-3">
            <div className="flex items-start gap-2">
              <i className="ri-shield-keyhole-fill text-[#FDB913] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-white/60 text-xs leading-relaxed">
                <strong className="text-white/80">Secure Registration.</strong> Your data is protected with 256-bit SSL encryption. Your email is verified instantly — no confirmation link required.
              </p>
            </div>
          </div>
        </div>

        {/* International Contact */}
        <div className="mt-4 bg-gradient-to-r from-[#FDB913]/10 to-[#FDB913]/5 border border-[#FDB913]/40 rounded-xl px-5 py-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#FDB913]/20 rounded-full border border-[#FDB913]/50 flex-shrink-0">
              <i className="ri-global-line text-[#FDB913] text-base"></i>
            </div>
            <div>
              <p className="text-white text-sm font-black tracking-wide">International Clients</p>
              <p className="text-white/55 text-xs">Need help in your language? Contact us directly.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <a
              href="tel:+15093960741"
              className="flex-1 flex items-center justify-center gap-2 bg-[#FF1A1A] hover:bg-[#cc0000] text-white font-black text-sm py-2.5 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-base"></i>
              Call: 1-(509) 396-0741
            </a>
            <a
              href="mailto:hello@mail.tariffrefund.attorney"
              className="flex-1 flex items-center justify-center gap-2 bg-[#FDB913]/20 hover:bg-[#FDB913]/30 border border-[#FDB913]/50 text-[#FDB913] font-black text-sm py-2.5 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line text-base"></i>
              Email Us
            </a>
          </div>
          <p className="text-white/40 text-[10px] text-center mt-2 leading-relaxed">
            Translation tools available &middot; Attorney speaks English only
          </p>
        </div>

        <div className="text-center mt-4">
          <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">
            <i className="ri-arrow-left-line mr-1"></i>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
